import def from "csi/def";
import { View } from "csi/def/View";
import { ControllerSaveFunction } from "csi/middleware/common";
import reflection from "csi/reflection";
import WorkingCache from "csi/state/WorkingCache";
import { C8Node, ICache, cnl } from "csi/typings";
import WizardStore from "designSystem/components/wizard/WizardStore";
import { ILocaleProvider } from "designSystem/locale";
import { action, computed, makeObservable, observable, observe, runInAction } from "mobx";
import { SyntheticEvent, createContext, useContext } from "react";
import CsiFormData from "csi/action/FormData";
import { ComponentItem } from "designSystem/types/component-item-types";
import JsonResponse from "csi/comm/JsonResponse";
import { getSourcesToHideOnCreateSharedDataSheetsWizard, createSharedDataSheets, getProductSourceCreationLevelString, getSourceLinkedHostDataSheetsMap, getSourceOptionsFromProducts, getSourceTable } from "./CreateSharedDataSheetsService";
import message from "csi/message";
import { Context } from "csi/def/Context";
import { ControllerInterface } from "csi/action/Controller";
import FormData from "csi/action/FormData";

export interface SourceTable {
	sourceId: cnl;

	sourceNode: C8Node;

	formData: CsiFormData;

	viewParams: Object;

	canHideTable: boolean;
}

export class CreateSharedDataSheetsWizardStore extends WizardStore {
	readonly ac: ControllerInterface;
	
	readonly #cache: ICache = new WorkingCache();

	readonly nodeUrl: string = "centric://APPAREL/Views";

	readonly viewId: string = "DSSelectionForSharedDSCreation";

	readonly titleRes: string = "CreateSharedDataSheets_Title";

	readonly switchLabel: string = this.locale.resource("OnlyNewSharedDataSheets");

	/** URL of SR/SRM */
	readonly actionSourceNodeUrl: string;

	/** FormData for the wizard */
	formData: CsiFormData;

	/** Product type derived from SR RequestType */
	productType: string;

	/** Products in the SR */
	productOptions: ComponentItem[];

	@observable refreshCounter: number = new Date().getTime();

	/** Product options that are selected in the first step of the wizard */
	@observable.ref selectedProductOptions: ComponentItem[];

	/** Option for Product Source of the SR */
	@observable.ref sourceOptions: ComponentItem[];

	/** Source options that are selected in the first step of the wizard */
	@observable.ref selectedSourceOptions: ComponentItem[];

	/** Observable to enable or disable finish button */
	@observable.ref selectedDataSheets: C8Node[];

	/** Observable for state of switch to show only new shared dataSheets */
	@observable showOnlyNewSharedDataSheets: boolean = true;

	/** Observable for getting SourceLinkedHostDataSheet map to pass to table controller */
	@observable.ref sourceLinkedHostDatasheetMap: Object;

	@observable.ref sourceTables: SourceTable[];

	constructor(
		ac: ControllerInterface,
		cache:ICache,
		onClose: () => void,
		onSave: ControllerSaveFunction,
		locale: ILocaleProvider,
		formData: CsiFormData,
		sourceOptions: ComponentItem[],
		productType: string,
		productOptions: ComponentItem[],
		actionSourceNodeUrl: string,
		
	) {
		super(2, "CreateSharedDataSheets", onClose, onSave, locale);
		makeObservable(this);
		this.ac = ac;
		this.#cache = cache;
		this.formData = formData;
		this.sourceOptions = sourceOptions;
		this.selectedSourceOptions = sourceOptions;
		this.productType = productType;
		this.productOptions = productOptions;
		this.selectedProductOptions = this.productOptions;
		this.selectedDataSheets = [];
		this.actionSourceNodeUrl = actionSourceNodeUrl;
		this.sourceTables = [];
	}

	@action toggleDataSheets = () : void => {
		this.sourceTables.forEach((table) => {
			table.viewParams["defView"].classControllerParams.showOnlyNewSharedDataSheets = !this.showOnlyNewSharedDataSheets;
		});
		this.showOnlyNewSharedDataSheets = !this.showOnlyNewSharedDataSheets;
		this.refreshCounter++;
	};

	/** When style select dropdown value is changed, update variables
	 * 	Update sourceOptions based on products selected when in SRM
	 */
	@action onChangeProducts = (ev: SyntheticEvent, val: ComponentItem[]) : void => {
		this.selectedProductOptions = val;
		if (this.#cache.get(this.actionSourceNodeUrl).$Type === "SupplierRequestMaster") {
			this.sourceOptions = getSourceOptionsFromProducts(this.actionSourceNodeUrl, this.selectedProductOptions.map((p) => p.node), this.#cache, this.locale);
			this.selectedSourceOptions = this.sourceOptions.length === 1 ? this.sourceOptions : [];
		}
	};

	@action onChangeSources = (ev: SyntheticEvent, val: ComponentItem[]) : void => {
		this.selectedSourceOptions = val;
	};

	@computed get selectedProducts(): cnl[] {
		return this.selectedProductOptions.map((option) => option.id);
	}

	get selectedSources(): cnl[] {
		return this.selectedSourceOptions.map((s) => s.id);
	}

	/**Get table view definition to show on 2nd page of wizard and return Table View params */
	viewParamsBySource = (fd: CsiFormData, source: cnl): Object => {
		fd.onSelect = this.onSelect;
		const rc = reflection.getClass("_CS_ApparelViews");
		const defView = def.getClassViewDef(rc, "DSSelectionForSharedDSCreation") as View;
		defView.classControllerParams = { 
			productSourceCreationLevel: getProductSourceCreationLevelString(this.actionSourceNodeUrl, this.#cache),
			selectedProducts: this.selectedProducts,  
			productType: this.productType, 
			dataSheetType: this.dataSheetType, 
			selectedSourceUrl: source, 
			sourceLinkedHostDatasheetMap: this.sourceLinkedHostDatasheetMap, 
			showOnlyNewSharedDataSheets: this.showOnlyNewSharedDataSheets, 
		};
		defView.setFormData(fd);
		return {
			bPopup: true,
			defView: defView,
		};
	};

	/** Make data sheet type as ComponentItem for dropdown */
	get dataSheetOption(): ComponentItem {
		return {
			id: this.locale.type(this.dataSheetType),
			name: this.locale.type(this.dataSheetType),
			value: this.locale.type(this.dataSheetType),
		};
	}

	/** Get localized string for dropdown label */
	get localizedProductType(): string {
		return this.locale.type(this.productType, true);
	}

	/** Get localized string for dropdown label */
	get localizedProductSource(): string {
		return this.locale.type("ProductSource");
	}

	/** Get localized string for dropdown label */
	get localizedDataSheetType(): string {
		return this.locale.resource("DataSheetType");
	}

	/** Get type of DataSheet */
	get dataSheetType(): string {
		return "ApparelBOM";
	}

	protected onSavePromise = async (): Promise<JsonResponse> => {
		const sourceToDataSheetsMap = new Map(Object.entries(this.selectedItems));
		try {
			const res = await createSharedDataSheets(sourceToDataSheetsMap);
			return res;
		} catch (err) {
			message.errorDialog({ message: err.message });
			return null;
		}
	};

	protected onNextPage = async (index: number): Promise<boolean> => {
		this.currentStepInfo.values.push(this.localizedProductType + " " + this.locale.resource("Selected") + ": " + this.selectedProductOptions.map((s) => s.node.$Name).join(", "));
		this.currentStepInfo.values.push(this.locale.resource("NumberOfSources") + ": " + this.selectedSourceOptions.length);
		this.currentStepInfo.values.push(this.localizedDataSheetType + ": " + this.locale.type(this.dataSheetType));
		this.sourceLinkedHostDatasheetMap = await getSourceLinkedHostDataSheetsMap(this.selectedSources, this.selectedProducts);
		const getMapToHideSources = await getSourcesToHideOnCreateSharedDataSheetsWizard(this.selectedSources, this.selectedProducts);
		const tables = await this.getSourceTables(getMapToHideSources);
		runInAction(() => {
			this.sourceTables = tables;
		});
		return true;
	};

	protected getSourceTables = async (getMapToHideSources: Object): Promise<SourceTable[]> => {
		const tables = await Promise.all(this.selectedSourceOptions.map(async (s) => {
			const fd = new FormData(this.ac);
			const canHideTable = getMapToHideSources[s.id];
			const sourceTable = getSourceTable(s, fd, this.viewParamsBySource(fd, s.id), canHideTable);
			return sourceTable;
		}));
		return tables;
	};

	protected _disableFinishButton = (): boolean => {
		if (this.index === 1) {
			return this.selectedDataSheets.length === 0;
		}
		return false;
	};

	protected _disablePrimaryButton = () : boolean => {
		if (this.index === 0) {
			return !this.selectedProductOptions.length || !this.selectedSourceOptions.length;
		}
		return false;
	};

	get selectedItems(): Object {
		const selectedItems = {};
		this.sourceTables.forEach((table) => {
			selectedItems[table.sourceId] = table.formData.getSelectedNodes().map((n) => n.$URL);
		});
		return selectedItems;
	}

	private onSelect = (ctx: Context, url: string, bChecked: boolean, bOld: boolean): void => {
		this.selectedDataSheets = Object.values(this.selectedItems);
	};
}

export const CreateSharedDataSheetsWizardStoreContext = createContext<CreateSharedDataSheetsWizardStore>(undefined);
export const useCreateSharedDataSheetsStore = (): CreateSharedDataSheetsWizardStore => useContext(CreateSharedDataSheetsWizardStoreContext);
